import { useRef, useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import { Add, Delete, Download, ReceiptLong, UploadFile } from '@mui/icons-material'

import {
    useCreateReconSourceMutation,
    useDeleteReconSourceMutation,
    useDownloadReconciliationCsvMutation,
    useGetReconciliationQuery,
    useListReconSourcesQuery,
    useUploadOrdersCsvMutation,
} from '@/apis/opsApi'
import { ORDER_PRESETS, type OrderPreset, type ReconSource } from '@/apis/opsTypes'
import { v2ErrorMessage } from '@/apis/v2Mappers'
import { useAppDispatch } from '@/app/hooks'
import { showNotification } from '@/app/notificationSlice'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { CopyButton } from '@/components/shared/CopyButton'
import { useReportArgs } from '@/hooks/useReportArgs'
import {
    Kpi,
    ReportStatus,
    SectionCard,
    fmtDateTime,
    fmtMoney,
    fmtNum,
    fmtPct,
} from '@/modules/reports/common'
import { downloadText } from '@/utils/download'
import type { Period } from '@/utils/dates'

const DISCREPANCY_LABEL: Record<string, string> = {
    value_mismatch: 'Value differs',
    missing_in_yom: 'Missing in YOM',
    extra_in_yom: 'Extra in YOM',
    duplicate: 'Duplicate',
    status_conflict: 'Status conflict',
    missing_transaction_id: 'No transaction id',
    refund_missing_in_yom: 'Refund missing',
    refund_value_mismatch: 'Refund differs',
    refund_extra_in_yom: 'Extra refund',
}

/** CSV uploads are capped at 8 MB by the API; refuse earlier with a clear message. */
const MAX_CSV_BYTES = 8 * 1024 * 1024

function SourcesSection({ projectId }: { projectId: string }) {
    const { data = [], isLoading, error } = useListReconSourcesQuery(projectId)
    const [createSource, createState] = useCreateReconSourceMutation()
    const [deleteSource] = useDeleteReconSourceMutation()
    const [open, setOpen] = useState(false)
    const [form, setForm] = useState({ name: '', preset: 'generic' as OrderPreset, secret: '' })
    const [created, setCreated] = useState<ReconSource | null>(null)
    const [toDelete, setToDelete] = useState<ReconSource | null>(null)

    const create = async () => {
        try {
            const s = await createSource({
                projectId,
                name: form.name.trim(),
                preset: form.preset,
                ...(form.secret.trim() ? { secret: form.secret.trim() } : {}),
            }).unwrap()
            setCreated(s)
            setOpen(false)
            setForm({ name: '', preset: 'generic', secret: '' })
        } catch {
            // shown from createState.error
        }
    }

    return (
        <SectionCard
            title="Order webhooks"
            action={
                <Button size="small" startIcon={<Add />} onClick={() => setOpen(true)}>
                    New source
                </Button>
            }
        >
            {created && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCreated(null)}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Point your order system&apos;s order webhooks at:
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            fontFamily: 'monospace',
                        }}
                    >
                        {created.url}
                        <CopyButton text={created.url} />
                    </Box>
                    {created.secret && (
                        <>
                            <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>
                                Signing secret (shown once):
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    fontFamily: 'monospace',
                                }}
                            >
                                {created.secret}
                                <CopyButton text={created.secret} />
                            </Box>
                        </>
                    )}
                </Alert>
            )}
            <ReportStatus
                isLoading={isLoading}
                error={error}
                isEmpty={data.length === 0}
                emptyTitle="No order webhooks"
                emptyDescription="Signed webhooks keep supplied orders current without uploads."
            >
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Preset</TableCell>
                            <TableCell>Webhook URL</TableCell>
                            <TableCell>Last received</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map(s => (
                            <TableRow key={s.id}>
                                <TableCell>{s.name}</TableCell>
                                <TableCell>{s.preset}</TableCell>
                                <TableCell sx={{ maxWidth: 320 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography
                                            variant="caption"
                                            noWrap
                                            sx={{ fontFamily: 'monospace' }}
                                            title={s.url}
                                        >
                                            {s.url}
                                        </Typography>
                                        <CopyButton text={s.url} />
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    {s.last_received_at ? fmtDateTime(s.last_received_at) : 'never'}
                                </TableCell>
                                <TableCell align="right">
                                    <Tooltip title="Delete source">
                                        <IconButton
                                            size="small"
                                            aria-label={`Delete ${s.name}`}
                                            onClick={() => setToDelete(s)}
                                        >
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </ReportStatus>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>New order webhook</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {createState.error && (
                        <Alert severity="error">{v2ErrorMessage(createState.error)}</Alert>
                    )}
                    <TextField
                        size="small"
                        label="Name"
                        value={form.name}
                        onChange={e => setForm({ ...form, name: e.target.value })}
                        sx={{ mt: 1 }}
                    />
                    <TextField
                        size="small"
                        select
                        label="Preset"
                        value={form.preset}
                        onChange={e => setForm({ ...form, preset: e.target.value as OrderPreset })}
                    >
                        {ORDER_PRESETS.map(p => (
                            <MenuItem key={p} value={p}>
                                {p}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        size="small"
                        label="Sender's signing secret"
                        type="password"
                        autoComplete="off"
                        required={form.preset !== 'generic'}
                        helperText={
                            form.preset === 'generic'
                                ? 'Optional: leave empty and YOM generates one.'
                                : `The ${form.preset} webhook signing secret.`
                        }
                        value={form.secret}
                        onChange={e => setForm({ ...form, secret: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={
                            createState.isLoading ||
                            (form.preset !== 'generic' && !form.secret.trim())
                        }
                        onClick={() => {
                            void create()
                        }}
                    >
                        Create
                    </Button>
                </DialogActions>
            </Dialog>
            <ConfirmDialog
                open={!!toDelete}
                title="Delete order webhook?"
                message="Requests to its URL are rejected from now on. Orders already received stay."
                confirmLabel="Delete"
                confirmColor="error"
                onConfirm={() => {
                    if (toDelete) {
                        void deleteSource({ projectId, id: toDelete.id })
                    }
                    setToDelete(null)
                }}
                onCancel={() => setToDelete(null)}
            />
        </SectionCard>
    )
}

/** v2 `/reports/reconciliation`: supplied orders vs exact purchase/refund events. */
export function ReconciliationTab({ period }: { period: Period }) {
    const args = useReportArgs(period)
    const dispatch = useAppDispatch()
    const [fresh, setFresh] = useState(false)
    const { data, isLoading, isFetching, error } = useGetReconciliationQuery({ ...args, fresh })
    const [downloadCsv, csvState] = useDownloadReconciliationCsvMutation()
    const [upload, uploadState] = useUploadOrdersCsvMutation()
    const [preset, setPreset] = useState<OrderPreset>('generic')
    const fileRef = useRef<HTMLInputElement>(null)

    const onFile = async (file: File) => {
        if (file.size > MAX_CSV_BYTES) {
            dispatch(
                showNotification({
                    message: 'The file is larger than 8 MB: split it and upload the parts.',
                    severity: 'error',
                }),
            )
            return
        }
        try {
            const r = await upload({
                projectId: args.projectId,
                csv: await file.text(),
                preset,
                subTenant: args.subTenant,
            }).unwrap()
            setFresh(true)
            dispatch(
                showNotification({
                    message: `${fmtNum(r.accepted)} orders accepted, ${fmtNum(r.rejected)} rejected${
                        r.errors[0] ? ` (row ${r.errors[0].row}: ${r.errors[0].error})` : ''
                    }`,
                    severity: r.rejected > 0 ? 'warning' : 'success',
                }),
            )
        } catch (err) {
            dispatch(
                showNotification({
                    message: v2ErrorMessage(err) ?? 'Upload failed',
                    severity: 'error',
                }),
            )
        }
    }

    const exportCsv = async () => {
        try {
            const csv = await downloadCsv(args).unwrap()
            downloadText(`reconciliation-${args.from}-${args.to}.csv`, csv)
        } catch (err) {
            dispatch(
                showNotification({
                    message: v2ErrorMessage(err) ?? 'Download failed',
                    severity: 'error',
                }),
            )
        }
    }

    const s = data?.summary
    const cur = data?.currency ?? 'USD'
    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
                <TextField
                    size="small"
                    select
                    label="CSV format"
                    value={preset}
                    onChange={e => setPreset(e.target.value as OrderPreset)}
                    sx={{ minWidth: 150 }}
                >
                    {ORDER_PRESETS.map(p => (
                        <MenuItem key={p} value={p}>
                            {p === 'generic'
                                ? 'Generic'
                                : p === 'shopify'
                                  ? 'Shopify export'
                                  : 'Stripe export'}
                        </MenuItem>
                    ))}
                </TextField>
                <input
                    ref={fileRef}
                    type="file"
                    accept=".csv,text/csv"
                    hidden
                    aria-label="Orders CSV file"
                    onChange={e => {
                        const input = e.currentTarget
                        const f = input.files?.[0]
                        // Clear so picking the same file again re-uploads it.
                        input.value = ''
                        if (f) {
                            void onFile(f)
                        }
                    }}
                />
                <Button
                    variant="contained"
                    startIcon={<UploadFile />}
                    disabled={uploadState.isLoading}
                    onClick={() => fileRef.current?.click()}
                >
                    {uploadState.isLoading ? 'Uploading…' : 'Upload orders CSV'}
                </Button>
                <Button
                    startIcon={<Download />}
                    disabled={csvState.isLoading || !data || data.discrepancies_total === 0}
                    onClick={() => {
                        void exportCsv()
                    }}
                >
                    Discrepancies CSV
                </Button>
            </Box>
            <ReportStatus
                isLoading={isLoading}
                error={error}
                isEmpty={
                    !!data && data.orders_supplied.total === 0 && data.summary.yom_purchases === 0
                }
                emptyIcon={<ReceiptLong />}
                emptyTitle="Nothing to reconcile yet"
                emptyDescription="Upload an orders export, or add an order webhook below. Each order is matched to the purchase event with the same transaction_id."
            >
                {data && s && (
                    <Box sx={{ opacity: isFetching ? 0.7 : 1 }}>
                        {data.warnings.map(w => (
                            <Alert key={w} severity="warning" sx={{ mb: 1 }}>
                                {w}
                            </Alert>
                        ))}
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <Kpi
                                    title="Coverage"
                                    value={fmtPct(s.coverage)}
                                    exact
                                    hint={`${fmtNum(s.matched)} of ${fmtNum(s.orders)} orders have a YOM purchase`}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <Kpi
                                    title="Revenue delta"
                                    value={fmtMoney(s.revenue_delta, cur)}
                                    hint={`YOM ${fmtMoney(s.yom_value, cur)} vs orders ${fmtMoney(s.orders_value, cur)} (${fmtPct(s.revenue_delta_pct)})`}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <Kpi
                                    title="Missing in YOM"
                                    value={fmtNum(s.missing_in_yom)}
                                    hint={fmtMoney(s.missing_value, cur)}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <Kpi
                                    title="Extra in YOM"
                                    value={fmtNum(s.extra_in_yom + s.duplicates)}
                                    hint={`${fmtNum(s.extra_in_yom)} unknown orders, ${fmtNum(s.duplicates)} duplicates`}
                                />
                            </Grid>
                        </Grid>
                        <SectionCard
                            title={`Discrepancies (${fmtNum(data.discrepancies_total)})`}
                            action={
                                <Typography variant="caption" color="text.secondary">
                                    {fmtNum(data.orders_supplied.total)} orders supplied
                                    {data.orders_supplied.last_updated_at
                                        ? ` · updated ${fmtDateTime(data.orders_supplied.last_updated_at)}`
                                        : ''}
                                </Typography>
                            }
                        >
                            {data.discrepancies.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    Every supplied order matches a YOM purchase.
                                </Typography>
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Type</TableCell>
                                            <TableCell>Order</TableCell>
                                            <TableCell>Created</TableCell>
                                            <TableCell align="right">Order value</TableCell>
                                            <TableCell align="right">YOM value</TableCell>
                                            <TableCell align="right">Events</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.discrepancies.map(d => (
                                            <TableRow
                                                key={`${d.type}-${d.sub_tenant ?? ''}-${d.order_id}-${d.event_ids}`}
                                            >
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        variant="outlined"
                                                        label={DISCREPANCY_LABEL[d.type] ?? d.type}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ fontFamily: 'monospace' }}>
                                                    {d.order_id || '—'}
                                                    {d.sub_tenant ? ` · ${d.sub_tenant}` : ''}
                                                </TableCell>
                                                <TableCell>
                                                    {d.order_created_at
                                                        ? fmtDateTime(d.order_created_at)
                                                        : '—'}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {fmtMoney(
                                                        d.order_value,
                                                        d.order_currency ?? cur,
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {fmtMoney(d.yom_value, cur)}
                                                </TableCell>
                                                <TableCell align="right">{d.yom_events}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                            {data.discrepancies_total > data.discrepancies.length && (
                                <Typography variant="caption" color="text.secondary">
                                    Showing {data.discrepancies.length} of{' '}
                                    {fmtNum(data.discrepancies_total)}; download the CSV for all.
                                </Typography>
                            )}
                        </SectionCard>
                    </Box>
                )}
            </ReportStatus>
            <Box sx={{ mt: 2 }}>
                <SourcesSection projectId={args.projectId} />
            </Box>
        </Box>
    )
}
